import { createTheme } from '@mui/material/styles';
import { colors } from '../config/colors';

/**
 * MUI theme and common component styles.
 * All MUI component overrides and shared input/class styles live here.
 */
const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
      '2xl': 1792, // Custom for consistency or matching tailwind if needed
      '3xl': 1920,
      '4xl': 2540,
    },
  },
  palette: {
    primary: {
      main: colors.primary[500],
      light: colors.primary[400],
      dark: colors.primary[600],
      50: colors.primary[50],
      100: colors.primary[100],
      200: colors.primary[200],
      700: colors.primary[700],
      800: colors.primary[800],
      950: colors.primary[950],
    },
    secondary: {
      main: colors.secondary[500],
      light: colors.secondary[400],
      dark: colors.secondary[600],
      50: colors.secondary[50],
      100: colors.secondary[100],
    },
    error: {
      main: colors.danger[500],
      light: colors.danger[100],
      dark: colors.danger[600],
    },
    grey: {
      200: colors.gray[200] || '#e5e7eb',
      300: '#d1d5db',
      500: '#6b7280',
      700: '#374151',
      900: '#111827',
    },
  },
  shape: {
    borderRadius: 12,
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    button: {
      textTransform: 'none',
      fontWeight: 700,
    },
  },
  components: {
    /** Common TextField / Input styles – used by TextInput and any MUI input */
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            backgroundColor: '#fff',
            '& fieldset': {
              borderWidth: 2,
              borderColor: colors.gray[200] || '#e5e7eb',
            },
            '&:hover': {
              backgroundColor: colors.primary[50],
              '& fieldset': {
                borderColor: colors.primary[200],
              },
            },
            '&.Mui-focused fieldset': {
              borderWidth: 2,
              borderColor: colors.primary[500],
              boxShadow: `0 0 0 2px ${colors.primary[50]}`,
            },
            '&.Mui-error fieldset': {
              borderColor: colors.danger[500],
            },
            '&.Mui-focused.Mui-error fieldset': {
              borderColor: colors.danger[500],
            },
          },
          '& .MuiInputLabel-outlined': {
            fontWeight: 600,
          },
          '& .MuiFormHelperText-root': {
            marginLeft: 0,
          },
        },
      },
      defaultProps: {
        variant: 'outlined',
        fullWidth: true,
        size: 'medium',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: '#fff',
          minHeight: 45,
          '& fieldset': {
            borderWidth: 2,
            borderColor: colors.gray[200] || '#e5e7eb',
          },
          '&:hover': {
            backgroundColor: colors.primary[50],
            '& fieldset': {
              borderColor: colors.primary[200],
            },
          },
          '&.Mui-focused fieldset': {
            borderWidth: 2,
            borderColor: colors.primary[500],
            boxShadow: `0 0 0 2px ${colors.primary[50]}`,
          },
          '&.Mui-error fieldset': {
            borderColor: colors.danger[500],
          },
        },
        input: {
          padding: '14px 16px',
          fontSize: '1rem',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        outlined: {
          fontWeight: 600,
          '&.MuiInputLabel-shrink': {
            fontWeight: 600,
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: colors.danger[500],
          fontSize: '0.75rem',
          marginTop: 4,
        },
      },
    },
    /** Checkbox – e.g. Remember me on Login; primary color when checked */
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: colors.gray[200],
          padding: 8,
          '&:hover': {
            backgroundColor: colors.primary[50],
          },
          '&.Mui-checked': {
            color: colors.primary[500],
            '&:hover': {
              backgroundColor: colors.primary[50],
            },
          },
          '&.Mui-disabled': {
            color: colors.gray[200],
          },
        },
      },
      defaultProps: {
        disableRipple: false,
        size: 'small',
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          marginLeft: -4,
          marginRight: 0,
        },
        label: {
          fontSize: '0.875rem',
          fontWeight: 500,
          color: colors.gray[700] || '#374151',
        },
      },
    },
    /** Select / Dropdown – same height (45px) and look as Input */
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          minHeight: 45,
        },
        select: {
          padding: '14px 16px',
          minHeight: 45,
          boxSizing: 'border-box',
        },
      },
      defaultProps: {
        variant: 'outlined',
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          '&.MuiMenu-paper': {
            borderRadius: 12,
            border: `2px solid ${colors.primary[200]}`,
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          },
        },
      },
    },
    /** Tooltip – used on icon buttons (e.g. Admin Courses table) */
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: '0.75rem',
          fontWeight: 600,
          padding: '6px 10px',
          borderRadius: 8,
          backgroundColor: colors.gray[900] || '#111827',
        },
        arrow: {
          color: colors.gray[900] || '#111827',
        },
      },
      defaultProps: {
        placement: 'top',
        arrow: true,
      },
    },
    /** Inner layout AppBar (admin/user header) */
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: '#fff',
          color: theme.palette.grey[700] || '#374151',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          borderBottom: `1px solid ${theme.palette.grey[200] || '#e5e7eb'}`,
          '& .MuiToolbar-root': {
            minHeight: 56,
            [theme.breakpoints.up('sm')]: { minHeight: 64 },
            paddingLeft: 16,
            paddingRight: 16,
            [theme.breakpoints.up('sm')]: { paddingLeft: 24, paddingRight: 24 },
          },
          '& .InnerHeader-profileBtn': {
            backgroundColor: theme.palette.primary[50],
            color: theme.palette.primary.dark || theme.palette.primary.main,
            '&:hover': { backgroundColor: theme.palette.primary[100] },
          },
        }),
      },
    },
    /** Profile dropdown Menu (from InnerHeader) */
    MuiMenu: {
      styleOverrides: {
        paper: {
          marginTop: 12,
          minWidth: 240,
          borderRadius: 8,
          boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
          border: '1px solid',
          borderColor: colors.gray[200] || '#e5e7eb',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          paddingTop: 12,
          paddingBottom: 12,
          gap: 12,
          '&.Mui-selected': {
            backgroundColor: colors.primary[50],
            '&:hover': { backgroundColor: colors.primary[100] },
          },
          '&:hover': { backgroundColor: colors.primary[50] },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 36,
        },
      },
    },
    /** Sidebar Drawer (admin/user) – paper gradient; width/transition stay in component */
    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme }) => ({
          overflowX: 'hidden',
          borderRight: `1px solid ${theme.palette.divider}`,
          background: `linear-gradient(180deg, ${theme.palette.primary[800]} 0%, ${theme.palette.primary[950]} 100%)`,
          color: '#fff',
        }),
      },
    },
    /** Sidebar nav items – use class Sidebar-navItem + Sidebar-navItemActive when active */
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          marginBottom: 4,
          '&.Sidebar-navItem': {
            backgroundColor: 'transparent',
            color: 'rgba(255,255,255,0.85)',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.1)',
              color: '#fff',
            },
            '&.Sidebar-navItemActive': {
              backgroundColor: 'rgba(255,255,255,0.15)',
              color: '#fff',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
            },
            '& .MuiListItemIcon-root': {
              color: 'inherit',
              justifyContent: 'center',
            },
          },
        },
      },
    },
  },
});

export default theme;
